import { useCallback, useEffect, useMemo, useState } from 'react'
import { v4 as uuidv4 } from 'uuid'
import { useTranslation } from 'react-i18next'
import { FileRejection, useDropzone } from 'react-dropzone'

import { FILE_COUNT_ERROR_CODE, FILE_INVALID_TYPE_ERROR_CODE, FILE_SIZE_ERROR_CODE, MAX_FILE_NUMBER, MAX_FILE_NUMBER_ERROR, MB, SUPPORTED_FILE_TYPES } from 'utils/constants'
import { ERRORS } from 'locales/locales'
import { uploadActions } from 'app/slices/upload.slice'
import { useAppDispatch, useAppSelector } from 'app/hooks'
import { conversationActions } from 'app/slices/conversations.slice'

import DropdownAlert from 'components/dropdown-alert/DropdownAlert'
import { FixMeLater } from 'types'
interface IProps {
  id: string
  isNewsFeed?: boolean
}

const useUploadFiles = ({ id }: IProps) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const { queue } = useAppSelector((state) => state.upload)
  const { draftData } = useAppSelector((state) => state.message)
  const systemData = useAppSelector((state) => state.system.data)

  const [isDragging, setDragging] = useState(false)

  const roomId = useMemo(() => location.pathname.split('/')?.[2], [location.pathname])
  const acceptableExtensions = useMemo(() => (systemData?.fileExtensions ?? []).map((extension: string) => extension.toLowerCase()), [systemData?.fileExtensions])

    const dropZoneFormatFromConstants = useMemo(() =>
        Object.assign({},
          ...SUPPORTED_FILE_TYPES.flatMap((category) =>
            category.extensions.map((ext) => {
              return { [ext.value]: [`${ext.name}`] }
            }),
          ),
        ),
      [SUPPORTED_FILE_TYPES],
    )

  const extensions = useMemo(() => {
    if (!acceptableExtensions || acceptableExtensions.length === 0) return {}
    const result: FixMeLater = {}
    Object.entries(dropZoneFormatFromConstants).forEach(([mimeType, extensions]) => {
      const matchingExtensions = acceptableExtensions.includes(mimeType)
      if (matchingExtensions) result[mimeType] = extensions
    })
    return result
  }, [acceptableExtensions, dropZoneFormatFromConstants])


  const onDropRejected = useCallback((fileRejections: Array<FileRejection>) => {
    
    if (fileRejections.some((file: FileRejection) => file?.errors?.some((error: any) => error?.code === FILE_COUNT_ERROR_CODE))) {
      DropdownAlert.ref.show({
        isError: true,
        message: t(ERRORS.FILE_COUNT_ERROR, { count: MAX_FILE_NUMBER }),
      })
      throw new Error(t(ERRORS.FILE_COUNT_ERROR, { count: MAX_FILE_NUMBER }))
    }
    if (fileRejections.some((file: FileRejection) => file?.errors?.some((error: any) => error?.code === FILE_SIZE_ERROR_CODE))) {
      DropdownAlert.ref.show({
        isError: true,
        message: t(ERRORS.FILE_SIZE_ERROR, {
          fileSize: systemData?.fileSizeLimit,
          fileSizeType: 'MB',
          file_name: fileRejections.map((file: FileRejection) => file?.file?.name)?.join(', '),
        }),
      })
      return
    }
  }, [t, dispatch, systemData?.fileSizeLimit])

  const onDropAccepted = useCallback((files: Array<File>) => {
    if (
      files.length > MAX_FILE_NUMBER ||
      queue.length > MAX_FILE_NUMBER ||
      draftData[roomId]?.files?.length > MAX_FILE_NUMBER ||
      draftData[roomId]?.files?.length + files.length > MAX_FILE_NUMBER
    ) {
      DropdownAlert.ref.show({ isError: true, message: t(ERRORS.FILE_COUNT_ERROR, { count: MAX_FILE_NUMBER }) })
      return
    } else {
      files.forEach((file: File) => {
        const startAt = new Date().toISOString()
        const uniqueId = uuidv4()
        dispatch(uploadActions.addQueue({ file, id, roomId, uniqueId, startAt }))
        dispatch(conversationActions.addFileInDraft({ file, id, roomId, uniqueId, startAt }))
      })
    }
  }, [t, queue.length, draftData[roomId]?.files?.length, roomId, dispatch])

  //AUDIO UPLOAD
  const onUploadAudio = useCallback((audioData: Blob) => {
    const file = new File([audioData], 'audio.mp3', {
      lastModified: new Date().getTime(),
      type: 'audio/mpeg',
    })
    
    // Check total file count including existing files in draft and queue
    const currentDraftFilesCount = draftData[roomId]?.files?.length || 0
    const currentQueueCount = queue.length
    
    if (currentDraftFilesCount + currentQueueCount >= MAX_FILE_NUMBER) {
      DropdownAlert.ref.show({
        isError: true,
        message: t(ERRORS.FILE_COUNT_ERROR, { count: MAX_FILE_NUMBER }),
      })
      return
    }
    
    // Process only the new audio file
    const startAt = new Date().toISOString()
    const uniqueId = uuidv4()
    dispatch(conversationActions.addFileInDraft({ uniqueId, file, id, roomId, startAt }))
    dispatch(uploadActions.addQueue({ uniqueId, file, id, roomId, startAt }))
  }, [t, dispatch, id, roomId, draftData, queue.length])



  const { getRootProps, getInputProps } = useDropzone({
    maxFiles: MAX_FILE_NUMBER,
    maxSize: parseInt(systemData?.fileSizeLimit ?? 50) * MB,
    accept: extensions,
    onDropRejected,
    onDropAccepted,
    onDrop: (acceptedFiles, fileRejections) => {
      if (Object.keys(extensions).length === 0) {
        DropdownAlert.ref.show({
          isError: true,
          message: t(ERRORS.UNSUPPORTED_FILE_TYPE_ERROR, {
            file_name: t(ERRORS.NO_FILE_TYPES_ALLOWED),
          }),
        })
        throw new Error(
          t(ERRORS.UNSUPPORTED_FILE_TYPE_ERROR, {
            file_name: t(ERRORS.NO_FILE_TYPES_ALLOWED),
          }),
        )
      }
      const includeFolder = acceptedFiles?.some(
        (file: FixMeLater) =>
          typeof file?.path === 'string' && (file.path.match(/\//g)?.length ?? 0) >= 1, //path içerisinde birden fazla "/" olmamalı eğer varsa bu bir folderdan gelmiştir.
      ) // Checks if any file is part of a folder
      if (includeFolder && fileRejections?.length === 0) {
        DropdownAlert.ref.show({
          isError: true,
          message: t(ERRORS.UPLOAD_FOLDER),
        })
        throw new Error(t(ERRORS.UPLOAD_FOLDER))
      }
      if (fileRejections?.length) {
        if (
          fileRejections?.some((file: FileRejection) =>
            file?.errors?.some((error: any) => error?.code === FILE_INVALID_TYPE_ERROR_CODE),
          )
        ) {
          //TODO: Arapça eklenecek.
          DropdownAlert.ref.show({
            isError: true,
            message: t(ERRORS.UNSUPPORTED_FILE_TYPE_ERROR, {
              file_name: fileRejections?.map((file: FileRejection) => file?.file?.name)?.join(', '),
            }),
          })
/*           throw new Error(t(ERRORS.UNSUPPORTED_FILE_TYPE_ERROR, {
            file_name: fileRejections?.map((file: FileRejection) => file?.file?.name)?.join(', '),
          })) */
        }
        if (
          fileRejections?.some((file: FileRejection) =>
            file?.errors.some(
              (error: any) =>
                error?.code === FILE_COUNT_ERROR_CODE || error?.code === FILE_SIZE_ERROR_CODE,
            ),
          )
        ) {
          onDropRejected(fileRejections)
          return
        }
      }
    },
  })

  const onDragAndDropEnd = useCallback(() => {
    setDragging(false)
  }, [isDragging])

  const onDragEnter = useCallback(() => {
    setDragging(true)
  }, [isDragging])

  const onDragOver = useCallback(() => {
    setDragging(true)
  }, [isDragging])

  const onDragLeave = useCallback(() => {
    setDragging(false)
  }, [isDragging])

  const onDrop = useCallback(() => {
    setDragging(false)
  }, [isDragging])

  return {
    isDragging,
    getInputProps,
    onDragEnter,
    onDragOver,
    onDragLeave,
    onUploadAudio,
    onDrop,
    getRootProps,
    onDragAndDropEnd,
  }
}

export default useUploadFiles
